import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@store/authStore'
import {
  isFirebaseConfigured,
  signInToFirebase,
  registerWebPush,
  onForegroundMessage,
} from '@services/firebase'

/**
 * App-wide Firebase bootstrap, mounted once in MainLayout alongside the SignalR connections.
 * When the user is authenticated and Firebase is configured: signs into Firebase with a
 * backend-minted custom token, registers the web-push device token, and wires the foreground FCM
 * handler to refresh the conversation list (badges). No-op when Firebase isn't configured.
 * Runs in parallel with SignalR during the migration cutover.
 */
export function useFirebaseBootstrap() {
  const { isAuthenticated } = useAuthStore()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!isFirebaseConfigured || !isAuthenticated) return

    let unsubscribe: (() => void) | undefined
    let cancelled = false

    ;(async () => {
      const signedIn = await signInToFirebase()
      if (!signedIn || cancelled) return

      // Best-effort push registration (prompts for permission if not yet decided).
      registerWebPush().catch(() => {})

      unsubscribe = await onForegroundMessage(() => {
        // Firestore onSnapshot already updates the open thread; just refresh sidebar unread badges.
        queryClient.invalidateQueries({ queryKey: ['chatConversations'] })
      })
    })()

    return () => {
      cancelled = true
      if (unsubscribe) unsubscribe()
    }
  }, [isAuthenticated, queryClient])
}
